/* =====================================================================
   PhantomR — global utilities (deferred, vanilla, dependency-free)
   Exposes window.PhantomR.utils for sections; auto-inits reveal,
   carousels, tabs, accordions and the overlay/scroll-lock manager.
   ===================================================================== */
(function () {
  "use strict";

  var root = document.documentElement;
  var prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var animationsOn = root.getAttribute("data-animations") !== "false" && !prefersReduced;

  /* ----------------------------------------------------- scroll lock + overlay */
  var lockCount = 0;
  var overlay = function () { return document.querySelector("[data-overlay]"); };

  function lockScroll() {
    if (lockCount === 0) {
      document.body.style.paddingRight = (window.innerWidth - root.clientWidth) + "px";
      document.body.style.overflow = "hidden";
    }
    lockCount++;
  }
  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
  }
  function showOverlay() { var o = overlay(); if (o) o.hidden = false; }
  function hideOverlay() { var o = overlay(); if (o) o.hidden = true; }

  /* ----------------------------------------------------------------- focus trap */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function trapFocus(container, opener) {
    var nodes = container.querySelectorAll(FOCUSABLE);
    if (!nodes.length) return function () {};
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    (container.querySelector("[autofocus]") || first).focus();

    function onKey(e) {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener("keydown", onKey);
    return function release() {
      container.removeEventListener("keydown", onKey);
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }

  /* ------------------------------------------------------------- scroll reveal */
  function initReveal() {
    var items = document.querySelectorAll("[data-reveal]");
    if (!items.length) return;
    if (!animationsOn || !("IntersectionObserver" in window)) {
      items.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ----------------------------------------------------------------- carousels */
  /* Markup: [data-carousel] wraps [data-carousel-track] (a .rail) and
     optional [data-carousel-prev] / [data-carousel-next] buttons. */
  function initCarousel(scope) {
    (scope || document).querySelectorAll("[data-carousel]").forEach(function (carousel) {
      if (carousel.__init) return;
      carousel.__init = true;
      var track = carousel.querySelector("[data-carousel-track]");
      /* Controls may live inside [data-carousel] OR in a sibling header
         (e.g. [data-carousel-controls]); fall back to the section scope. */
      var ctrlScope = carousel.closest("section") || carousel.parentElement || carousel;
      var prev = carousel.querySelector("[data-carousel-prev]") || ctrlScope.querySelector("[data-carousel-prev]");
      var next = carousel.querySelector("[data-carousel-next]") || ctrlScope.querySelector("[data-carousel-next]");
      if (!track) return;

      function step() {
        var item = track.querySelector(":scope > *");
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 24;
        return item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      }
      function update() {
        var maxScroll = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= maxScroll;
      }
      if (prev) prev.addEventListener("click", function () { track.scrollBy({ left: -step(), behavior: prefersReduced ? "auto" : "smooth" }); });
      if (next) next.addEventListener("click", function () { track.scrollBy({ left: step(), behavior: prefersReduced ? "auto" : "smooth" }); });
      track.addEventListener("scroll", function () { window.requestAnimationFrame(update); }, { passive: true });
      window.addEventListener("resize", update);
      update();
    });
  }

  /* ---------------------------------------------------------------------- tabs */
  /* Markup: [data-tabs] > [role=tablist] > button[role=tab][aria-controls]
     plus [role=tabpanel][id] panels. */
  function initTabs(scope) {
    (scope || document).querySelectorAll("[data-tabs]").forEach(function (group) {
      if (group.__init) return;
      group.__init = true;
      var tabs = Array.prototype.slice.call(group.querySelectorAll('[role="tab"]'));
      function select(tab) {
        tabs.forEach(function (t) {
          var selected = t === tab;
          t.setAttribute("aria-selected", selected);
          t.tabIndex = selected ? 0 : -1;
          var panel = document.getElementById(t.getAttribute("aria-controls"));
          if (panel) panel.hidden = !selected;
        });
      }
      tabs.forEach(function (tab, i) {
        tab.addEventListener("click", function () { select(tab); });
        tab.addEventListener("keydown", function (e) {
          var dir = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          var n = (i + dir + tabs.length) % tabs.length;
          tabs[n].focus(); select(tabs[n]);
        });
      });
    });
  }

  /* expose */
  window.PhantomR = window.PhantomR || {};
  window.PhantomR.utils = {
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    showOverlay: showOverlay,
    hideOverlay: hideOverlay,
    trapFocus: trapFocus,
    animationsOn: animationsOn,
    prefersReduced: prefersReduced,
    formatMoney: formatMoney,
    initCarousel: initCarousel,
    initTabs: initTabs,
    initReveal: initReveal
  };

  /* --------------------------------------------------------------- money fmt */
  function formatMoney(cents, format) {
    var fmt = format || (window.PhantomR && window.PhantomR.moneyFormat) || "${{amount}}";
    var value = (cents / 100).toFixed(2);
    var parts = value.split(".");
    var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return fmt.replace(/\{\{\s*amount\s*\}\}/g, dollars + "." + parts[1])
              .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, dollars);
  }

  /* ------------------------------------------------------------------- init */
  function dismiss() { document.dispatchEvent(new CustomEvent("phantomr:dismiss")); }

  function init() {
    root.classList.add("js");
    initReveal();
    initCarousel();
    initTabs();
    var o = overlay();
    if (o) o.addEventListener("click", dismiss);
    document.addEventListener("keydown", function (e) { if (e.key === "Escape") dismiss(); });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else { init(); }

  /* Re-init on Shopify theme editor section load */
  document.addEventListener("shopify:section:load", function (e) {
    initCarousel(e.target);
    initTabs(e.target);
    initReveal();
  });
})();
